import { Controller } from "@hotwired/stimulus"

// Builds the Doctolib public profile URL from the practitioner name and billing
// city, the way Doctolib builds it: /<specialty>/<city>/<first-last>. It is only
// a guess (namesakes get a numeric suffix, the practice city may differ):
// - on creation, the field is prefilled with it, unknown parts left as
//   placeholders, and follows the name and city until the admin edits it;
// - on edition, it is offered as a link to check.
// Mirrors +Profiles::Practitioner::DOCTOLIB_URL_PLACEHOLDERS+.
const CITY_PLACEHOLDER = "VILLE"
const NAME_PLACEHOLDER = "PRENOM-NOM"

export default class extends Controller {
  static targets = ["input", "suggestion", "link"]
  static values = {
    specialty: { type: String, default: "osteopathe" },
    prefill: { type: Boolean, default: false },
    firstNameField: String,
    lastNameField: String,
    cityField: String,
    firstName: String,
    lastName: String,
    city: String
  }

  connect () {
    this.form = this.element.closest("form") || this.element
    this.prefilledUrl = null
    this.refresh = this.refresh.bind(this)
    this.events = ["input", "change", "address-autocomplete:change"]
    this.events.forEach((event) => this.form.addEventListener(event, this.refresh))
    this.refresh()
  }

  disconnect () {
    this.events.forEach((event) => this.form.removeEventListener(event, this.refresh))
  }

  use () {
    this.inputTarget.value = this.suggestedUrl
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  refresh () {
    if (this.prefillValue) this.prefill()

    this.suggestedUrl = this.buildUrl()
    const show = !this.prefillValue && Boolean(this.suggestedUrl) && this.inputTarget.value.trim() === ""

    this.suggestionTarget.hidden = !show
    if (!show) return

    this.linkTarget.href = this.suggestedUrl
    this.linkTarget.textContent = this.suggestedUrl
  }

  // Clearing the field counts as editing it: the template does not come back.
  prefill () {
    if (this.edited) return

    const current = this.inputTarget.value.trim()
    if (this.prefilledUrl === null ? current : current !== this.prefilledUrl) {
      this.edited = true
      return
    }

    const url = this.buildUrl({ placeholders: true })
    if (url === current) return

    this.inputTarget.value = url
    this.prefilledUrl = url
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  buildUrl ({ placeholders = false } = {}) {
    const name = slug(`${this.read(this.firstNameFieldValue, this.firstNameValue)} ${this.read(this.lastNameFieldValue, this.lastNameValue)}`)
    const city = slug(this.read(this.cityFieldValue, this.cityValue))
    const complete = name.includes("-") && Boolean(city)
    if (!complete && !placeholders) return ""

    return `https://www.doctolib.fr/${this.specialtyValue}/${city || CITY_PLACEHOLDER}/${name.includes("-") ? name : NAME_PLACEHOLDER}`
  }

  // Form fields win over the saved values: they carry what the admin is typing.
  read (fieldId, fallback) {
    const field = this.form.querySelector(`[id="${fieldId}"]`)
    return field?.value?.trim() || fallback
  }
}

function slug (text) {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}
